import { createAsyncThunk } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'
import {
  clearPatientEvent,
  getChargeMDM,
  getPatientsVisitDetailsByVisitId,
  getVisitHistory,
  updateChargeMDM,
} from '../../helper/backendHelper'
import { dateDbFormat } from '../../utils/dateTimeUtils'
import { VISIT_STATUS } from '../../utils/statusConstants'
import { showLongTermCare } from '../../utils/sharedFeaturesFunctions'
import { COLLECTOR_URL } from '../../apiHelper/apiRoute/collector'
import { getCollector, postCollector, putCollector } from '../../apiHelper/collectorApi'
import { deformatPhoneNumber } from '../../utils/sharedFunctions'
import FEATURES from '../../utils/featuresConstants'
import { getQualityMips } from '../../helper/mipsHelper'
import { getFusion } from '../../apiHelper/fusionApi'
import { updatePatientEventFacilityDetails } from '../../helper/fusionHelper'
import { trimPatientObject } from '../../components/common/shared/sharedPatientCode'

export const getEncounter = createAsyncThunk(
  'sidePanel/getEncounter',
  async (encounterId, thunkAPI) => {
    try {
      if (!encounterId) {
        return null
      }

      if (Boolean(thunkAPI.getState()?.sidePanel?.panelEncounter)) {
        return thunkAPI.rejectWithValue(null)
      }

      const isLast = thunkAPI.getState()?.sidePanel?.isLastEncounter

      const results = await Promise.allSettled([
        getPatientsVisitDetailsByVisitId(encounterId).then((res) => res?.result?.[0]),
        getVisitHistory(encounterId).then((res) => res?.resData),
        isLast
          ? null
          : getCollector(COLLECTOR_URL.GET_PROGRESS_NOTE.replace('{visitID}', encounterId)).then(
              (res) => (res?.status === 'failure' ? null : res?.data),
            ),
      ])
      const [currentVisit, visitHistory, currentChart] = results.map((result) => {
        if (result.status === 'fulfilled') {
          return result.value
        }
        console.error(result.reason)
        return null
      })

      setTimeout(() => thunkAPI.dispatch(updatePanelMips()), 1000)

      return {
        currentVisit,
        visitHistory,
        currentChart,
      }
    } catch (err) {
      console.error('getting error in rendering sidePanel', err)
      return null
    }
  },
)

export const refreshEncounter = createAsyncThunk(
  'sidePanel/refreshEncounter',
  async ({ encounterId, maintainLastEncounter = false, useAiCpt = false }, thunkAPI) => {
    try {
      // refetch current encounter and chart
      const results = await Promise.allSettled([
        getPatientsVisitDetailsByVisitId(encounterId).then((res) => res?.result?.[0]),
        getCollector(COLLECTOR_URL.GET_PROGRESS_NOTE.replace('{visitID}', encounterId)).then(
          (res) => (res?.status === 'failure' ? null : res?.data),
        ),
      ])
      const [refreshedEncounter, refreshedChart] = results.map((result) => {
        if (result.status === 'fulfilled') {
          return result.value
        }
        console.error(result.reason)
        return null
      })

      if (useAiCpt) {
        const newAiCPT = thunkAPI.getState()?.sidePanel?.panelEncounter?.aiCptCodeCheck
        if (newAiCPT) {
          // TODO add feature flag check for selection?
          let cpts = [...(refreshedEncounter?.procedureCodes || [])]
          if (cpts?.[0]?.em_code) {
            cpts[0] = newAiCPT
          } else {
            cpts = [newAiCPT, ...cpts]
          }
          refreshedEncounter.procedureCodes = cpts

          // save CPTs to the DB too, since Fusion doesnt
          setTimeout(() => thunkAPI.dispatch(saveEncounterField({ procedureCodes: cpts })), 1000)
        }
      }

      return { refreshedEncounter, refreshedChart, maintainLastEncounter, useAiCpt }
    } catch (err) {
      console.error('getting error in saving sidePanel refreshEncounter', err)
      return null
    }
  },
)

const VALID_MDM_KEYS = ['complexity', 'data', 'risk']
export const updateMDM = createAsyncThunk('sidePanel/updateMDM', async (updatedMDM, thunkAPI) => {
  try {
    const encounter = thunkAPI.getState()?.sidePanel?.panelEncounter

    // fetch MDM if empty body
    if (Object.keys(updatedMDM).length === 0) {
      const mdmValues = await getChargeMDM(encounter?._id)
      if (mdmValues?.status === 'failure' || mdmValues?.response?.status) {
        // failed
        return thunkAPI.rejectWithValue(mdmValues?.response?.data)
      }
      return mdmValues
    }

    // update MDM if body not empty
    const reformattedMDM = {}
    VALID_MDM_KEYS.forEach((mdmKey) => {
      reformattedMDM[mdmKey] = {
        user_level: updatedMDM[mdmKey]?.user_level || updatedMDM[mdmKey]?.level,
      }
    })

    let newMDM = await updateChargeMDM(encounter?._id, reformattedMDM)

    if (newMDM?.status === 'failure' || newMDM?.response?.status) {
      // failed
      return thunkAPI.rejectWithValue(newMDM.response?.data)
    }

    return newMDM
  } catch (err) {
    console.error('getting error in saving sidePanel updateMDM', err)
    return null
  }
})

export const savePatientDetails = createAsyncThunk(
  'sidePanel/savePatientDetails',
  async (data, thunkAPI) => {
    try {
      const encounter = thunkAPI.getState()?.sidePanel?.panelEncounter
      let payloadEdit = {
        dob:
          data.DOB || encounter?.patient_dob
            ? dateDbFormat(data.DOB || encounter?.patient_dob)
            : '',
        gender: data.gender || encounter?.gender || 'U',
        ethnicity: data.ethnicity || encounter?.ethnicity,
        genderIdentity: data.genderIdentity || encounter?.genderIdentity,
        preferredLanguage: data.preferredLanguage || encounter?.preferredLanguage,
        race: data.race || encounter?.race,
        sexualOrientation: data.sexualOrientation || encounter?.sexualOrientation,
        amendments: data.amendments || encounter?.amendments,
        // primary_care_physician: data.primary_care_physician || encounter?.primary_care_physician,
        first_name: data.firstname || encounter?.patient_firstname,
        last_name: data.lastname || encounter?.patient_lastname,
        middle_name: data.middlename || encounter?.patient_middlename,
        hospice_related: Boolean(encounter?.hospice_related),
        acoEnrolled: Boolean(data.acoEnrolled || encounter?.patient_acoEnrolled),
        hospiceEnrolled: Boolean(data.hospiceEnrolled || encounter?.patient_hospiceEnrolled),
        admission_reason: encounter?.admission_reason || '',
        cellphone: deformatPhoneNumber(encounter.patient_cellphone || ''),
        room: encounter?.facility_mrn || '',
        patient_admit_info: [
          {
            facility_id: data.facility_id || encounter?.facility_id || '',
            facility_name: data.facility_name || encounter?.facility_name || '',
            facility_mrn: data.facility_mrn || encounter?.facility_mrn || '',
            admission_date: data.admission_date || encounter?.admission_date || null,
            discharge_date: data.discharge_date || encounter?.discharge_date || null,
          },
        ],
        // encounter?.patient_admit_info?.map((pai) => ({
        //   facility_id: pai.facility_id || '',
        //   facility_name: pai.facility_name || '',
        //   facility_mrn: pai.facility_mrn || '',
        //   admission_date: pai.admission_date || null,
        //   discharge_date: pai.discharge_date || null,
        // })) || [],
        //

        //     [
        //   {
        //     facility_id: data.selectedFacility?._id || '',
        //     facility_name: data.selectedFacility?.name || '',
        //     admission_date:
        //       data.DOI !== null && data.DOI !== ''
        //         ? format(new Date(data.DOI), 'yyyy-MM-dd')
        //         : '',
        //     facility_mrn: data.FacilityMRN?.trim(),
        //   },
        // ],
      }

      const url = COLLECTOR_URL.EDIT_PATIENT.replace('{patient_id}', `${encounter?.patient_id}`)
      let res = await putCollector(url, payloadEdit)

      if (res.status === 'success') {
        thunkAPI.dispatch(
          refreshEncounter({
            encounterId: encounter?._id,
            maintainLastEncounter: true,
            useAiCpt: true,
          }),
        )
      } else {
        // TODO toast?
      }
    } catch (err) {
      console.error('getting error in saving sidePanel patient', err)
      return null
    }
  },
)

let isCloning = false
export const saveEncounterField = createAsyncThunk(
  'sidePanel/saveEncounterField',
  async (data, thunkAPI) => {
    try {
      if (isCloning) {
        return data
      }

      const isLast = thunkAPI.getState()?.sidePanel?.isLastEncounter
      const encounter = thunkAPI.getState()?.sidePanel?.panelEncounter
      const features = thunkAPI.getState()?.user?.features || []

      let procedures = encounter.procedureCodes?.map((proc) => ({
        code: proc.code + '' || '',
        modifiers: proc.modifiers,
        units: proc?.units,
      }))

      if (isLast) {
        // clone
        isCloning = true
        // since we are cloning, save our payload to the store
        // to avoid being overwritten by other dispatches
        thunkAPI.dispatch(saveEncounterField(data))

        const url = COLLECTOR_URL.CLONE.replace('{visitId}', `${encounter._id}`)
        const req = {
          service_date:
            data.field === 'service_date_from' ? data.value : encounter.service_date_from,
          status: VISIT_STATUS.checked_in,
          last_visit: null,
          procedures: procedures,
          encounterType: encounter?.encounterType || 'Subsequent',
        }
        const res = await postCollector(url, req)
        isCloning = false

        if (res.status === 'failure') {
          const msg = res?.message || 'Failed to create visit'
          toast.error(msg)
          return thunkAPI.rejectWithValue({ type: 'clone', error: msg })
        } else {
          toast.success('New Visit Created')
        }

        // in case any other actions have occurred during our clone
        // re-save the encounter after the store has updated our _id
        setTimeout(() => {
          thunkAPI.dispatch(saveEncounterField({}))
        }, 1000)

        return {
          cloneUpdates: [
            // values to update that the clone set
            { payload: { field: '_id', value: res.result._id } },
            { payload: { field: 'status', value: res.result.status } },
            {
              payload: { field: 'rendering_provider_id', value: res.result.rendering_provider_id },
            },
            {
              payload: {
                field: 'rendering_provider_name',
                value: res.result.rendering_provider_name,
              },
            },
            // and remove fields that should not carry over with a clone
            { payload: { field: 'coding_ai', value: undefined } },
            { payload: { field: 'aiCptCodeCheck', value: undefined } },
            { payload: { field: 'aiDxCodes', value: undefined } },
            { payload: { field: 'dischargePatientEvent', value: undefined } },
          ],
        }
      } else {
        if (!encounter.facility_id) {
          return toast.error('No facility selected')
        }

        // Make sure that our visit does not remain in an 'Admit' Status
        if (encounter?.status === VISIT_STATUS.admit) {
          const url = COLLECTOR_URL.VISIT_STATUS.replace('{visitId}', encounter?._id)
          await putCollector(
            url,
            // the status we would prefer the visit to update to
            // if its in an 'unseen' state
            { status: VISIT_STATUS.checked_in },
          )
        }

        // save
        const saveBody = {
          admission_date: encounter?.admission_date || '',
          admission_reason: encounter?.admission_reason || '',
          // VERIFY
          // only append admitPatientEvent if the feature is on, and it has been pulled in
          ...(features?.includes(FEATURES.InsuranceMapper) &&
            encounter?.admitPatientEvent?._id && {
              admitPatientEvent: encounter?.admitPatientEvent._id,
            }),
          // verify TODO
          ...(features?.includes(FEATURES.InsuranceMapper) &&
            encounter?.dischargePatientEvent?._id && {
              dischargePatientEvent: encounter?.dischargePatientEvent._id,
            }),
          appointment_duration: '',
          appointment_time: '',
          care_coordination: Boolean(encounter.care_coordination),
          careteam: encounter.careteam || '',
          charge_id: encounter._id,
          code_assist: encounter?.code_assist || {
            diagnosis_notes: '',
            billing_notes: '',
            audio_notes: [],
            templates: [],
          },
          diagnosis_codes: encounter.diagnosisCodes.map((item) => item.code) || [],
          discharge_date: encounter?.discharge_date || '',
          encounterType: encounter.encounterType || 'Initial',
          facility_id: encounter.facility_id,
          facility_mrn: encounter?.facility_mrn || '',
          files: [], //files,
          // verify
          facility_name: encounter.facility_name,
          hospice_related: Boolean(encounter.hospice_related),
          incident_to_provider_id: encounter.incident_to_provider_id,
          incident_to_provider_substantive: encounter.incident_to_provider_id
            ? encounter.incident_to_provider_substantive
            : null,
          // only add to object if not null
          ...(encounter?.legal_entity_id && {
            legal_entity_id: encounter?.legal_entity_id,
          }),
          long_term_care:
            showLongTermCare(features, encounter?.place_of_service) && encounter.long_term_care,
          neuro_suite: features?.includes(FEATURES.NeuroSuite)
            ? encounter.neuro_suite || false
            : null,
          pending_cosign: encounter.pending_cosign,
          patient: trimPatientObject(encounter.pData),
          patient_id: encounter.patient_id,
          place_of_service: encounter?.place_of_service,
          // verify
          procedure_codes: procedures,
          references: encounter.references,
          room: encounter?.room && encounter?.room !== '-' ? encounter?.room : '',
          service_date: dateDbFormat(encounter.service_date_from),
          status: new Date(encounter.service_date_from).toDateString()
            ? VISIT_STATUS.checked_in
            : VISIT_STATUS.scheduled,
          telehealth: Boolean(encounter.telehealth),
          isSaveOnly: true,
        }

        let res = await postCollector(COLLECTOR_URL.ADD_ENCOUNTER, saveBody)

        if (res?.status === 'success') {
          thunkAPI.dispatch(updatePanelMips())

          if (encounter.admitPatientEvent === 'DISCHARGE') {
            clearPatientEvent(encounter._id, 'ADMIT')
          }

          // PatientEvent update for MRN and FacMRN
          updatePatientEventFacilityDetails({
            facility_mrn: encounter?.facility_mrn || '',
            room: encounter?.room && encounter?.room !== '-' ? encounter?.room : '',
            patient_id: encounter.patient_id,
            charge_id: encounter._id,
          })
        } else {
          if (res?.result || res.response?.data?.message) {
            let message = res?.result || res.response?.data?.message
            if (res.response?.data?.message?.includes('facility_mrn')) {
              message =
                'Facility MRN must be less than 15 characters, and only have letters, numbers, or hyphens'
            }
            toast.error(message)
          }
        }
      }

      return {
        ...data,
      }
    } catch (err) {
      console.error('getting error in rendering sidePanel', err)
      return null
    }
  },
)

export const updatePanelMips = createAsyncThunk(
  'sidePanel/updatePanelMips',
  async (_, thunkAPI) => {
    try {
      const practiceId = thunkAPI.getState()?.user?.practice?._id
      const encounterId = thunkAPI.getState()?.sidePanel?.panelEncounter?._id
      const features = thunkAPI.getState()?.user?.features || []

      if (features?.includes(FEATURES.VisitNotesMIPS) && encounterId && practiceId) {
        const newMips = await getQualityMips(encounterId, practiceId)
        return (newMips || []).some((mip) => !mip.value)
      }

      return false
    } catch (err) {
      console.error('getting error in updating sidePanel mips', err)
      return null
    }
  },
)
export const performAiCodeCheck = createAsyncThunk(
  'sidePanel/performAiCodeCheck',
  async (_, thunkAPI) => {
    let {
      user: { features },
      sidePanel,
    } = thunkAPI.getState()
    if (sidePanel?.panelChart?._id) {
      const res = await getFusion('v1/chart/inspect/' + sidePanel.panelChart._id)

      if (res?.status !== 'success') {
        console.error(res)
        return null
      } else {
        return res.data
      }
    }
    return null
  },
)
